import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileFixedCTA from '../../components/feature/MobileFixedCTA';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { createArticleStructuredData, createBreadcrumbStructuredData, createFAQStructuredData, createItemListStructuredData } from '../../utils/structuredData';

export default function EconomicIndicatorsPage() {
  // SEO設定
  usePageSEO({
    title: '経済指標の見方・活用方法｜FX初心者向け完全ガイド',
    description: 'FX取引で重要な経済指標の見方と活用方法を初心者向けに徹底解説。米国雇用統計、FOMC、CPIなど主要8指標の特徴、発表タイミング、トレード戦略を詳しく説明します。経済指標カレンダーの使い方も紹介。',
    keywords: '経済指標, FX, 雇用統計, FOMC, CPI, GDP, 見方, 活用方法',
    ogImage: 'https://public.readdy.ai/ai/img_res/4f6f83beeab3e34bd216448803ef8f01.jpg',
    structuredData: [
      createArticleStructuredData({
        headline: '経済指標の見方・活用方法',
        description: 'FX取引で重要な経済指標の見方と実践的な活用方法を解説。主要8指標の特徴、発表タイミング、トレード戦略、経済指標カレンダーの使い方を詳しく説明します。',
        image: 'https://readdy.ai/api/search-image?query=economic%20indicators&width=1200&height=630&seq=indicators-article&orientation=landscape',
        author: 'DMM FX情報局',
        datePublished: '2024-01-15',
        dateModified: new Date().toISOString().split('T')[0],
        url: '/economic-indicators'
      }),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: '経済指標', url: '/economic-indicators' }
      ]),
      createFAQStructuredData([
        {
          question: '経済指標とは何ですか？',
          answer: '経済指標とは、各国政府や中央銀行が発表する経済状況を示すデータのことです。FX市場では、この指標の結果によって通貨の価値が大きく変動します。経済指標は、金利政策の予測、トレンドの把握、リスク管理に役立ちます。'
        },
        {
          question: '最も重要な経済指標は何ですか？',
          answer: '最も重要な経済指標は、米国雇用統計、FOMC政策金利、消費者物価指数（CPI）の3つです。米国雇用統計は毎月第1金曜日に発表され、発表直後に50pips以上動くことも珍しくありません。FOMC政策金利は年8回発表され、金利の方向性が為替トレンドを左右します。CPIは毎月中旬に発表され、インフレ率を測る代表的な指標です。'
        },
        {
          question: '経済指標の発表時間はいつですか？',
          answer: '主な経済指標の発表時間（日本時間）は以下の通りです。米国雇用統計：21:30（夏時間）/22:30（冬時間）、FOMC声明：3:00（夏時間）/4:00（冬時間）、米国CPI：21:30（夏時間）/22:30（冬時間）、日銀政策決定会合：12:00頃（不定）。夏時間と冬時間で発表時間が1時間ずれるため、経済指標カレンダーで事前に確認しましょう。'
        },
        {
          question: '経済指標発表時の取引で注意すべきことは何ですか？',
          answer: '経済指標発表時は、発表30分前にポジションを縮小または決済する、発表直後の乱高下でエントリーしない、損切りを設定せずに放置しない、予想だけで事前にポジションを取らないことが重要です。初心者の方は、発表前後の取引は避けるのが無難です。発表5〜15分後に初動の方向を確認してからエントリーを検討しましょう。'
        },
        {
          question: '経済指標カレンダーはどこで確認できますか？',
          answer: '経済指標カレンダーは、Investing.com（https://jp.investing.com/economic-calendar/）などのサイトで確認できます。カレンダーでは、発表日時、重要度（星の数）、予想値、前回値をチェックできます。毎日の取引前、週末、夜（米国指標前）に確認する習慣をつけましょう。'
        }
      ]),
      createItemListStructuredData(
        '押さえるべき主要経済指標',
        [
          {
            name: '米国雇用統計',
            description: '非農業部門の雇用者数変化を示す最重要指標。毎月第1金曜日に発表され、為替市場に最も大きな影響を与える。発表直後に50pips以上動くことも珍しくない。',
            url: '/economic-indicators#indicators'
          },
          {
            name: 'FOMC政策金利',
            description: '米国の政策金利を決定する会合。年8回発表され、金利の方向性が為替トレンドを左右する。利上げ・利下げ発表で大きなトレンド転換も。',
            url: '/economic-indicators#indicators'
          },
          {
            name: '消費者物価指数（CPI）',
            description: 'インフレ率を測る代表的な指標。毎月中旬に発表され、金融政策の方向性を占う重要データ。予想との乖離で30-50pips程度の変動。',
            url: '/economic-indicators#indicators'
          },
          {
            name: 'GDP（国内総生産）',
            description: '国の経済規模と成長率を示す。四半期ごとに速報値・改定値・確定値の3回発表される。速報値で予想と大きく乖離すると影響大。',
            url: '/economic-indicators#indicators'
          },
          {
            name: '日銀金融政策決定会合',
            description: '日本の金融政策を決定。年8回発表され、円相場に直接影響を与える最重要イベント。サプライズ発表で円が急変動することも。',
            url: '/economic-indicators#indicators'
          },
          {
            name: 'ISM製造業景況指数',
            description: '製造業の景況感を示す先行指標。毎月第1営業日に発表され、50を上回れば景気拡大、下回れば縮小。予想との乖離で20-30pips程度の変動。',
            url: '/economic-indicators#indicators'
          },
          {
            name: '小売売上高',
            description: '個人消費の動向を示す。毎月中旬に発表され、米国GDPの約7割を占める消費の先行指標。予想との乖離で15-25pips程度の変動。',
            url: '/economic-indicators#indicators'
          },
          {
            name: '貿易収支',
            description: '輸出入の差額を示す。毎月発表され、長期的な通貨の需給バランスに影響。単独での影響は限定的だが、トレンド確認に有用。',
            url: '/economic-indicators#indicators'
          }
        ]
      )
    ]
  });

  const [activeTab, setActiveTab] = useState('importance');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const majorIndicators = [
    {
      name: '米国雇用統計',
      englishName: 'Non-Farm Payrolls',
      frequency: '毎月第1金曜日',
      importance: 5,
      description: '非農業部門の雇用者数変化を示す最重要指標。為替市場に最も大きな影響を与える。',
      impact: '発表直後に50pips以上動くことも珍しくない',
      icon: 'ri-user-line'
    },
    {
      name: 'FOMC政策金利',
      englishName: 'Federal Funds Rate',
      frequency: '年8回',
      importance: 5,
      description: '米国の政策金利を決定する会合。金利の方向性が為替トレンドを左右する。',
      impact: '利上げ・利下げ発表で大きなトレンド転換も',
      icon: 'ri-bank-line'
    },
    {
      name: '消費者物価指数（CPI）',
      englishName: 'Consumer Price Index',
      frequency: '毎月中旬',
      importance: 5,
      description: 'インフレ率を測る代表的な指標。金融政策の方向性を占う重要データ。',
      impact: '予想との乖離で30-50pips程度の変動',
      icon: 'ri-shopping-cart-line'
    },
    {
      name: 'GDP（国内総生産）',
      englishName: 'Gross Domestic Product',
      frequency: '四半期ごと',
      importance: 4,
      description: '国の経済規模と成長率を示す。速報値・改定値・確定値の3回発表される。',
      impact: '速報値で予想と大きく乖離すると影響大',
      icon: 'ri-bar-chart-box-line'
    },
    {
      name: '日銀金融政策決定会合',
      englishName: 'BOJ Policy Rate',
      frequency: '年8回',
      importance: 4,
      description: '日本の金融政策を決定。円相場に直接影響を与える最重要イベント。',
      impact: 'サプライズ発表で円が急変動することも',
      icon: 'ri-building-line'
    },
    {
      name: 'ISM製造業景況指数',
      englishName: 'ISM Manufacturing PMI',
      frequency: '毎月第1営業日',
      importance: 4,
      description: '製造業の景況感を示す先行指標。50を上回れば景気拡大、下回れば縮小。',
      impact: '予想との乖離で20-30pips程度の変動',
      icon: 'ri-building-4-line'
    },
    {
      name: '小売売上高',
      englishName: 'Retail Sales',
      frequency: '毎月中旬',
      importance: 3,
      description: '個人消費の動向を示す。米国GDPの約7割を占める消費の先行指標。',
      impact: '予想との乖離で15-25pips程度の変動',
      icon: 'ri-store-line'
    },
    {
      name: '貿易収支',
      englishName: 'Trade Balance',
      frequency: '毎月',
      importance: 3,
      description: '輸出入の差額を示す。長期的な通貨の需給バランスに影響。',
      impact: '単独での影響は限定的だが、トレンド確認に有用',
      icon: 'ri-ship-line'
    }
  ];

  const tradingStrategies = [
    {
      title: '発表前の準備',
      icon: 'ri-calendar-check-line',
      color: 'from-teal-500 to-emerald-600',
      points: [
        '経済指標カレンダーで発表日時を確認',
        '市場予想値（コンセンサス）をチェック',
        '前回値との比較で方向性を予測',
        'ポジションサイズを通常より小さく調整'
      ]
    },
    {
      title: '発表直後の対応',
      icon: 'ri-flashlight-line',
      color: 'from-amber-500 to-orange-600',
      points: [
        '結果と予想値の乖離を素早く確認',
        'スプレッドが広がるため即座のエントリーは避ける',
        '初動の方向を確認してから判断',
        '逆指値注文でリスク管理を徹底'
      ]
    },
    {
      title: '発表後の分析',
      icon: 'ri-line-chart-line',
      color: 'from-rose-500 to-pink-600',
      points: [
        '市場の反応が予想通りか確認',
        '他の指標との整合性をチェック',
        '中長期トレンドへの影響を分析',
        '次回発表に向けた学習・記録'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* パンくずリスト */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'FX基礎知識', url: '/fx-basic' },
            { name: '経済指標', url: '/economic-indicators' }
          ]} />
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Abstract%20financial%20data%20visualization%20with%20flowing%20lines%20and%20charts%20in%20teal%20and%20emerald%20colors%20on%20dark%20background%20representing%20economic%20indicators%20and%20forex%20trading%20analysis%20modern%20minimalist%20style&width=1920&height=800&seq=eco-hero-1&orientation=landscape"
            alt="経済指標の見方"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/70"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-teal-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <i className="ri-bar-chart-grouped-line text-teal-400 text-lg"></i>
              <span className="text-sm font-medium text-teal-300">FX初心者向けガイド</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              経済指標の見方・活用方法
            </h1>
            
            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
              FX取引で勝つために欠かせない経済指標の読み方と、<br className="hidden md:block" />
              実践的な活用方法を初心者にも分かりやすく解説します。
            </p>
            
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <a 
                href="#indicators"
                className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white px-8 py-4 rounded-lg text-base font-semibold hover:shadow-xl transition-all whitespace-nowrap cursor-pointer inline-flex items-center space-x-2"
              >
                <span>主要指標を見る</span>
                <i className="ri-arrow-down-line"></i>
              </a>
              <a 
                href="https://jp.investing.com/economic-calendar/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer border border-white/20"
              >
                経済指標カレンダー
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 経済指標とは Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">経済指標とは？</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              各国政府や中央銀行が発表する経済状況を示すデータのことです
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10 mb-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">なぜ重要なのか？</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  経済指標は、その国の経済状況を数値で表したものです。FX市場では、この指標の結果によって通貨の価値が大きく変動します。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <i className="ri-check-line text-teal-600"></i>
                    </div>
                    <p className="text-gray-700">
                      <strong className="text-gray-900">金利政策の予測</strong>：インフレ率や雇用統計から中央銀行の次の動きを予測
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <i className="ri-check-line text-teal-600"></i>
                    </div>
                    <p className="text-gray-700">
                      <strong className="text-gray-900">トレンドの把握</strong>：経済の強弱から中長期的な通貨の方向性を判断
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <i className="ri-check-line text-teal-600"></i>
                    </div>
                    <p className="text-gray-700">
                      <strong className="text-gray-900">リスク管理</strong>：発表前後の急変動に備えたポジション調整
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-lightbulb-line text-amber-500 mr-2"></i>
                  具体例で理解する
                </h4>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">米国雇用統計が予想より良い場合</p>
                    <div className="flex items-center">
                      <span className="text-gray-700">雇用増加</span>
                      <i className="ri-arrow-right-line text-gray-400 mx-2"></i>
                      <span className="text-gray-700">景気好調</span>
                      <i className="ri-arrow-right-line text-gray-400 mx-2"></i>
                      <span className="text-teal-600 font-bold">ドル高</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">日銀が利上げを発表した場合</p>
                    <div className="flex items-center">
                      <span className="text-gray-700">金利上昇</span>
                      <i className="ri-arrow-right-line text-gray-400 mx-2"></i>
                      <span className="text-gray-700">円の魅力UP</span>
                      <i className="ri-arrow-right-line text-gray-400 mx-2"></i>
                      <span className="text-teal-600 font-bold">円高</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* タブ切り替え */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('importance')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors cursor-pointer ${
                  activeTab === 'importance'
                    ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                重要度の見方
              </button>
              <button
                onClick={() => setActiveTab('timing')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors cursor-pointer ${
                  activeTab === 'timing'
                    ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                発表タイミング
              </button>
              <button
                onClick={() => setActiveTab('reading')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors cursor-pointer ${
                  activeTab === 'reading'
                    ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                数値の読み方
              </button>
            </div>
            
            <div className="p-6 md:p-8">
              {activeTab === 'importance' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-900">経済指標の重要度を理解する</h3>
                  <p className="text-gray-700 leading-relaxed">
                    経済指標には重要度があり、一般的に星の数（★）で表されます。重要度が高いほど、発表時の為替変動が大きくなる傾向があります。
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                      <div className="flex items-center mb-3">
                        <span className="text-amber-500 text-lg"><span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">★★★</span></span>
                        <span className="ml-2 text-sm font-semibold text-red-700 bg-red-100 px-2 py-1 rounded">最重要</span>
                      </div>
                      <p className="text-sm text-gray-700">
                        雇用統計、FOMC、CPIなど。発表前後は大きな変動に注意が必要。
                      </p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                      <div className="flex items-center mb-3">
                        <span className="text-amber-500 text-lg">★★☆</span>
                        <span className="ml-2 text-sm font-semibold text-amber-700 bg-amber-100 px-2 py-1 rounded">重要</span>
                      </div>
                      <p className="text-sm text-gray-700">
                        GDP、ISM指数など。予想との乖離が大きい場合に影響。
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                      <div className="flex items-center mb-3">
                        <span className="text-amber-500 text-lg">★☆☆</span>
                        <span className="ml-2 text-sm font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded">参考</span>
                      </div>
                      <p className="text-sm text-gray-700">
                        住宅指標、消費者信頼感など。単独での影響は限定的。
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {activeTab === 'timing' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-900">発表タイミングを把握する</h3>
                  <p className="text-gray-700 leading-relaxed">
                    経済指標は決まった日時に発表されます。特に米国の指標は日本時間の夜〜深夜に発表されることが多いです。
                  </p>
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">主な発表時間（日本時間）</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-700">米国雇用統計</span>
                        <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">21:30（夏時間）/ 22:30（冬時間）</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-700">FOMC声明</span>
                        <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">3:00（夏時間）/ 4:00（冬時間）</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-700">米国CPI</span>
                        <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">21:30（夏時間）/ 22:30（冬時間）</span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-gray-700">日銀政策決定会合</span>
                        <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded">12:00頃（不定）</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4">
                    <p className="text-sm text-gray-700">
                      <strong>ポイント：</strong>夏時間と冬時間で発表時間が1時間ずれます。経済指標カレンダーで事前に確認しましょう。
                    </p>
                  </div>
                </div>
              )}
              
              {activeTab === 'reading' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-900">数値の読み方をマスターする</h3>
                  <p className="text-gray-700 leading-relaxed">
                    経済指標で重要なのは「結果」と「予想」の比較です。予想より良ければポジティブ、悪ければネガティブと判断します。
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-3">
                          <i className="ri-arrow-up-line text-white text-xl"></i>
                        </div>
                        <h4 className="font-bold text-gray-900">ポジティブサプライズ</h4>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">結果が予想を上回った場合</p>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">例：米国雇用統計</p>
                        <p className="text-sm">予想: +15万人 → 結果: <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded text-lg">+25万人</span></p>
                        <p className="text-xs text-green-600 mt-2">→ ドル買い要因</p>
                      </div>
                    </div>
                    <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-3">
                          <i className="ri-arrow-down-line text-white text-xl"></i>
                        </div>
                        <h4 className="font-bold text-gray-900">ネガティブサプライズ</h4>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">結果が予想を下回った場合</p>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">例：米国雇用統計</p>
                        <p className="text-sm">予想: +15万人 → 結果: <span className="bg-red-100 text-red-700 font-bold px-2 py-1 rounded text-lg">+5万人</span></p>
                        <p className="text-xs text-red-600 mt-2">→ ドル売り要因</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-teal-50 border-l-4 border-teal-500 rounded-r-lg p-4">
                    <p className="text-sm text-gray-700">
                      <strong>注意：</strong>「前回値」との比較も重要です。予想通りでも前回より大幅に悪化していれば、ネガティブに反応することもあります。
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 主要経済指標一覧 Section */}
      <section id="indicators" className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">押さえるべき8つの主要指標</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              FX取引で特に注目すべき経済指標を重要度順に解説します
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {majorIndicators.map((indicator, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all p-6 border border-gray-100"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mr-4">
                      <i className={`${indicator.icon} text-white text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{indicator.name}</h3>
                      <p className="text-xs text-gray-500">{indicator.englishName}</p>
                    </div>
                  </div>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <i 
                        key={i}
                        className={`ri-star-fill text-sm ${i < indicator.importance ? 'text-amber-400' : 'text-gray-200'}`}
                      ></i>
                    ))}
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  {indicator.description}
                </p>
                
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center text-xs text-gray-500">
                    <i className="ri-calendar-line mr-1"></i>
                    {indicator.frequency}
                  </div>
                  <div className="text-xs font-medium">
                    <span className="bg-red-100 text-red-700 px-2 py-1 rounded">{indicator.impact}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 活用方法 Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">経済指標の実践的な活用方法</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              発表前・発表直後・発表後の3つのフェーズで適切に対応しましょう
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {tradingStrategies.map((strategy, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className={`bg-gradient-to-r ${strategy.color} p-6`}>
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mb-4">
                    <i className={`${strategy.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-white">{strategy.title}</h3>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {strategy.points.map((point, i) => (
                      <li key={i} className="flex items-start">
                        <i className="ri-check-line text-teal-500 mr-2 mt-0.5"></i>
                        <span className="text-sm text-gray-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* 利用者の声へのリンク */}
          <div className="mb-12 bg-white rounded-xl p-6 border-l-4 border-teal-500 shadow-md">
            <p className="text-gray-700 leading-relaxed">
              実際の利用者の声や評判については、こちらの記事で詳しくまとめています。<br />
              → <Link to="/reviews" className="text-teal-600 font-semibold hover:text-teal-700 underline cursor-pointer">「DMM FXの口コミ・評判は？実際どうなのか調査」</Link>
            </p>
          </div>

          {/* 実践例 */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-file-list-3-line text-teal-500 mr-3"></i>
              実践例：米国雇用統計発表時の対応
            </h3>
            
            <div className="grid md:grid-cols-4 gap-4 mb-8">
              <div className="bg-gray-50 rounded-xl p-5 text-center">
                <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white font-bold">1</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">発表30分前</p>
                <p className="text-xs text-gray-600">ポジションを縮小または決済</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-5 text-center">
                <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white font-bold">2</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">発表直後</p>
                <p className="text-xs text-gray-600">結果を確認、即座のエントリーは避ける</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-5 text-center">
                <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white font-bold">3</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">発表5〜15分後</p>
                <p className="text-xs text-gray-600">初動の方向を確認してエントリー検討</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-5 text-center">
                <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white font-bold">4</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">発表1時間後</p>
                <p className="text-xs text-gray-600">トレンド継続か反転かを判断</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-error-warning-line text-amber-500 mr-2"></i>
                初心者が避けるべき行動
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <i className="ri-close-line text-red-500 mr-2 mt-0.5"></i>
                  <span className="text-sm text-gray-700">発表直前に大きなポジションを持つ</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-close-line text-red-500 mr-2 mt-0.5"></i>
                  <span className="text-sm text-gray-700">発表直後の乱高下でエントリー</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-close-line text-red-500 mr-2 mt-0.5"></i>
                  <span className="text-sm text-gray-700">損切りを設定せずに放置</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-close-line text-red-500 mr-2 mt-0.5"></i>
                  <span className="text-sm text-gray-700">予想だけで事前にポジションを取る</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 経済指標カレンダーの使い方 Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">経済指標カレンダーの使い方</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              毎日の取引前に必ずチェックしましょう
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-teal-500 to-emerald-600 p-6">
              <h3 className="text-xl font-bold text-white flex items-center">
                <i className="ri-calendar-2-line mr-3"></i>
                カレンダーの見方
              </h3>
            </div>
            <div className="p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-bold text-gray-900 mb-4">チェックすべき項目</h4>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <span className="text-teal-600 font-bold text-sm">1</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">発表日時</p>
                        <p className="text-sm text-gray-600">日本時間で確認。夏時間・冬時間に注意</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <span className="text-teal-600 font-bold text-sm">2</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">重要度</p>
                        <p className="text-sm text-gray-600">星の数で表示。★★★は要注意</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <span className="text-teal-600 font-bold text-sm">3</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">予想値</p>
                        <p className="text-sm text-gray-600">市場のコンセンサス予想</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <span className="text-teal-600 font-bold text-sm">4</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">前回値</p>
                        <p className="text-sm text-gray-600">前回発表時の結果</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-4">おすすめの確認タイミング</h4>
                  <div className="bg-gray-50 rounded-xl p-5 space-y-4">
                    <div className="flex items-center">
                      <i className="ri-sun-line text-amber-500 mr-3 text-xl"></i>
                      <div>
                        <p className="font-semibold text-gray-900">朝（取引開始前）</p>
                        <p className="text-sm text-gray-600">その日の重要指標を確認</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-calendar-line text-teal-500 mr-3 text-xl"></i>
                      <div>
                        <p className="font-semibold text-gray-900">週末</p>
                        <p className="text-sm text-gray-600">翌週の重要イベントを把握</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-moon-line text-gray-500 mr-3 text-xl"></i>
                      <div>
                        <p className="font-semibold text-gray-900">夜（米国指標前）</p>
                        <p className="text-sm text-gray-600">21時以降の指標を再確認</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* カレンダーリンク */}
          <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6 text-center">
            <h4 className="font-bold text-gray-900 mb-4">経済指標カレンダーをチェック</h4>
            <a 
              href="https://jp.investing.com/economic-calendar/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-gradient-to-r from-teal-500 to-emerald-600 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transition-all cursor-pointer"
            >
              <i className="ri-external-link-line mr-2"></i>
              Investing.com 経済指標カレンダー
            </a>
          </div>
        </div>
      </section>

      {/* 関連記事 Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">関連記事</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/trading-hours" className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-white text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">DMM FXの取引時間</h3>
              <p className="text-sm text-gray-600 mb-4">取引時間と市場の特徴を詳しく解説</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
            <Link to="/fx-basics" className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-book-open-line text-white text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">FXとは？初心者向け超入門</h3>
              <p className="text-sm text-gray-600 mb-4">FXの基本から仕組みまで分かりやすく解説</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
            <Link to="/spread" className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-white text-xl"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">スプレッドは本当に狭い？</h3>
              <p className="text-sm text-gray-600 mb-4">DMM FXのスプレッドと取引コストを詳しく解説</p>
              <div className="flex items-center text-teal-600 text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* スマホ用固定CTAボタン */}
      <MobileFixedCTA />
      
      {/* 固定CTAの高さ分の余白（スマホのみ） */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}
